// A2PTrialBanner — persistent, in-flow bar under the topbar (pushes content
// down, never covers the page title). Fully DYNAMIC: it reads the tenant's real
// A2P stage (the same model that powers the /admin/a2p "Tenant A2P Status"
// dashboard) and shows the matching message. No trial countdown, no hand-coded
// special cases — whatever stage the admin page would show, this reflects.
//
//   • complete                        → nothing
//   • *_pending / profile_approved    → green "on track, no action" reassurance
//   • not_started / brand_approved    → amber "finish your A2P setup" call to action
//   • *_failed                        → red "needs attention" prompt to fix
//
// Stage + copy live in src/lib/a2pStage.js (mirrors src/api_routes/admin-a2p.js).
// (Filename kept for import stability; it's no longer trial-specific.)
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Check, AlertTriangle, ArrowRight } from 'lucide-react';
import { useGetLive } from './GetLiveContext';
import { a2pBannerContent } from '../../lib/a2pStage';

// Settings sections live on the single /settings page; ?section=a2p opens the A2P card.
const A2P_ROUTE = '/settings?section=a2p';

const TONE = {
  pending: { wrap: 'bg-surfox-greenSoft border-[#bbf7d0]', text: 'text-[#15803d]', sub: 'text-[#15803d]/80', Icon: Check,          stroke: 3 },
  action:  { wrap: 'bg-[#fef6e9] border-[#f6e0b6]',        text: 'text-[#92590a]', sub: 'text-[#92590a]/80', Icon: Shield,         button: 'Finish setup' },
  failed:  { wrap: 'bg-[#fff1f2] border-[#fecdd3]',        text: 'text-[#be123c]', sub: 'text-[#be123c]/80', Icon: AlertTriangle,  button: 'Review' },
};

export default function A2PTrialBanner() {
  const navigate = useNavigate();
  const status = useGetLive();

  if (!status || status.loading) return null;

  const content = a2pBannerContent(status.a2p?.stage);
  if (!content) return null; // complete (or unknown stage) → nothing to show

  const tone = TONE[content.tone];
  const { Icon } = tone;

  return (
    <div className={`flex-none flex items-center gap-3 h-12 px-[22px] border-b ${tone.wrap}`}>
      <Icon size={17} className={tone.text} strokeWidth={tone.stroke || 2} />
      <span className={`text-[13px] font-bold ${tone.text}`}>{content.title}</span>
      {content.subtitle && (
        <span className={`hidden sm:inline text-[12.5px] ${tone.sub}`}>{content.subtitle}</span>
      )}
      {tone.button && (
        <button
          onClick={() => navigate(A2P_ROUTE)}
          className="ml-auto flex items-center gap-1.5 h-8 px-3.5 rounded-lg bg-surfox-ink text-white text-[12.5px] font-bold hover:brightness-125"
        >
          {tone.button} <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}
